// ============================================
// Analytics Widget Data Types
// ============================================

/**
 * Filter operators for query conditions
 */
export type FilterOperator =
  | '='
  | '!='
  | '>'
  | '>='
  | '<'
  | '<='
  | 'in'
  | 'not_in'
  | 'like'
  | 'is_null'
  | 'is_not_null';

/**
 * Aggregation types for metric calculations
 */
export type AggregationType =
  | 'count'
  | 'count_distinct'
  | 'count_hll'
  | 'sum'
  | 'avg'
  | 'min'
  | 'max'
  | 'percentile';

/**
 * Sort direction for order_by clauses
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Filter condition for queries
 */
export interface QueryFilter {
  field: string;
  operator: FilterOperator;
  value: string | number | boolean | null | Array<string | number>;
}

/**
 * Aggregation definition
 */
export interface QueryAggregation {
  type: AggregationType;
  field?: string | null;
  alias: string;
}

/**
 * Time range for queries - relative or absolute
 */
export interface TimeRange {
  /** Relative time range (e.g., "1h", "24h", "7d", "30d", "90d", "1y") */
  relative?: string;
  /** Absolute start time (ISO 8601 format) */
  start?: string;
  /** Absolute end time (ISO 8601 format) */
  end?: string;
}

/**
 * Order by clause
 */
export interface OrderBy {
  field: string;
  direction: SortDirection;
}

// ============================================
// Scoping Types
// ============================================

/**
 * Scoping type determines data visibility perspective.
 * Fixed per widget template variant at creation time — not user-configurable.
 */
export type ScopingType = 'self' | 'manager' | 'scoped_hr' | 'global_hr' | 'executive' | 'admin';

/**
 * Query configuration for template-based widgets
 */
export interface TemplateQueryConfig {
  /** Pre-defined metric key from the registry */
  metric_key: string;
  /** Optional filters to apply */
  filters?: QueryFilter[];
  /** Optional dimensions to group by */
  dimensions?: string[];
  /** Optional time range */
  time_range?: TimeRange;
  /** Optional order by clauses */
  order_by?: OrderBy[];
  /** Optional result limit */
  limit?: number;
  /** Optional percentile value (for percentile aggregations) */
  percentile?: number;
}

/**
 * Query configuration for custom widgets
 */
export interface CustomQueryConfig {
  /** Table name to query */
  table: string;
  /** Aggregations to perform */
  aggregations: QueryAggregation[];
  /** Optional filters to apply */
  filters?: QueryFilter[];
  /** Optional dimensions to group by */
  dimensions?: string[];
  /** Optional time range */
  time_range?: TimeRange;
  /** Optional order by clauses */
  order_by?: OrderBy[];
  /** Optional result limit */
  limit?: number;
  /** Optional percentile value (for percentile aggregations) */
  percentile?: number;
}

/**
 * Union type for query configurations
 */
export type QueryConfig = TemplateQueryConfig | CustomQueryConfig;

/**
 * Request payload for widget data endpoint
 */
export interface WidgetDataRequest {
  query_config: QueryConfig;
  /** Owner user ID for shared dashboard context - when viewing another user's dashboard */
  owner_user_id?: string;
  /** Target data model (e.g., 'workforce', 'reviewcloud') */
  model?: string;
  /** Template scoping category (e.g., 'user_scoped', 'org_scoped') - from widget template */
  scoping?: string;
}

/**
 * Legacy metadata returned with widget data (pre-V2)
 */
export interface WidgetDataMetadataLegacy {
  query_time: string;
  row_count: number;
  total_rows?: number;
  sql?: string; // Only present in debug mode
}

/**
 * V2 API Response Meta
 */
export interface ResponseMeta {
  query_time_ms: number;
  row_count: number;
  total_rows?: number;
  page?: number;
  page_size?: number;
  has_more?: boolean;
}

/**
 * V2 API Column Data Types
 */
export type DataType = 'integer' | 'float' | 'string' | 'boolean' | 'datetime' | 'date';

/**
 * V2 API Column Schema
 */
export interface ColumnSchema {
  key: string;
  label: string;
  type: DataType;
  format?: string;
  sortable: boolean;
  hidden?: boolean;
}

/**
 * V2 API Response Schema
 */
export interface ResponseSchema {
  columns: ColumnSchema[];
}

/**
 * V2 Response from widget data endpoint
 */
export interface WidgetDataResponseV2<T = Record<string, unknown>> {
  data: T[];
  schema: ResponseSchema;
  meta: ResponseMeta;
  debug?: {
    sql: string;
  };
}

/**
 * Legacy response from widget data endpoint (pre-V2)
 */
export interface WidgetDataResponseLegacy<T = Record<string, unknown>> {
  data: T[];
  metadata: WidgetDataMetadataLegacy;
}

/**
 * Unified response type that supports both V2 and legacy formats
 * V2 format is the default, with backward-compatible metadata accessor
 */
export interface WidgetDataResponse<T = Record<string, unknown>> {
  data: T[];
  schema: ResponseSchema;
  meta: ResponseMeta;
  /** @deprecated Use meta instead - legacy compatibility accessor */
  metadata?: WidgetDataMetadataLegacy;
  debug?: {
    sql: string;
  };
}

/**
 * Type guard to check if response is V2 format
 */
export function isV2Response<T>(
  response: WidgetDataResponseV2<T> | WidgetDataResponseLegacy<T>
): response is WidgetDataResponseV2<T> {
  return 'schema' in response && 'meta' in response;
}

/**
 * Error response from widget data endpoint
 */
export interface WidgetDataError {
  error: string;
  status: number;
}

// ============================================
// Predefined Metric Keys
// ============================================

/**
 * Available headcount metric keys
 */
export type HeadcountMetricKey =
  | 'active_employee_count'
  | 'new_employee_count'
  | 'employee_gender_distribution'
  | 'employee_age_distribution'
  | 'employee_type_distribution'
  | 'employee_location_distribution'
  | 'employee_list';

/**
 * Goalboard metric keys (model_key: reviewcloud.goalboard)
 */
export type GoalboardMetricKey =
  | 'goalboard_employee_summary'
  | 'goalboard_goal_status_summary'
  | 'goalboard_goals_list'
  | 'goalboard_employees_without_goals_list'
  | 'goalboard_stale_goals_employees_list';

/**
 * Cycles metric keys (model_key: reviewcloud.cycles)
 */
export type CyclesMetricKey =
  | 'cycles_overview_summary'
  | 'cycles_active_count'
  | 'cycles_completion_average'
  | 'cycles_at_risk_count'
  | 'cycles_by_type'
  | 'cycles_by_completion'
  | 'cycles_list'
  | 'cycles_at_risk_list';

/**
 * Generic metric key type (can be extended with more metric types)
 */
export type MetricKey = HeadcountMetricKey | GoalboardMetricKey | CyclesMetricKey | string;

// ============================================
// Type Guards
// ============================================

/**
 * Type guard to check if a query config uses a template
 */
export function isTemplateQuery(config: QueryConfig): config is TemplateQueryConfig {
  return 'metric_key' in config;
}

/**
 * Type guard to check if a query config is custom
 */
export function isCustomQuery(config: QueryConfig): config is CustomQueryConfig {
  return 'table' in config;
}

// ============================================
// Common Response Types
// ============================================

/**
 * Employee count response (for active_employee_count metric)
 */
export interface EmployeeCountResponse {
  employee_count: number;
}

/**
 * Gender distribution response
 */
export interface GenderDistributionResponse {
  gender: string;
  employee_count: number;
}

/**
 * Age distribution response
 */
export interface AgeDistributionResponse {
  age_group: string;
  employee_count: number;
}

/**
 * Employment type distribution response
 */
export interface EmploymentTypeDistributionResponse {
  employment_type: string;
  employee_count: number;
}

/**
 * Employee list row response
 */
export interface EmployeeListRow {
  department_id: number;
  email: string;
  employment_type: string;
  event_time: number;
  fname: string;
  job_title: string | null;
  lname: string;
  manager_id: number;
  user_uuid: number;
  value: number;
}

/**
 * Location distribution response
 */
export interface LocationDistributionResponse {
  location: string;
  employee_count: number;
}

/**
 * Department distribution response
 */
export interface DepartmentDistributionResponse {
  department_id: number;
  employee_count: number;
}

/**
 * Time series data point
 */
export interface TimeSeriesDataPoint {
  date: string;
  employee_count: number;
}

// ============================================
// Goalboard Widget Response Types
// ============================================

/**
 * Goalboard Employee Summary response (multi_stat)
 * Filters: cycle_id, department_id, location_id
 */
export interface GoalboardEmployeeSummaryResponse {
  total_employees: number;
  with_goals: number;
  without_goals: number;
  goals_not_updated: number;
}

/**
 * Goal Status Summary item (donut/pie/bar)
 * Filters: cycle_id, department_id, location_id
 */
export interface GoalStatusSummaryItem {
  status: string;
  count: number;
  percentage: number;
}

/**
 * Goals List row (table)
 * Columns: title, employee_name, status, priority, progress, updated_at
 * Filters: cycle_id, status, priority, department_id
 */
export interface GoalsListRow {
  title: string;
  employee_name: string;
  status: string;
  priority: string;
  progress: number;
  updated_at: string;
}

/**
 * Employees Without Goals row (table)
 * Columns: employee_name, employee_email, department_name, job_title
 * Filters: cycle_id, department_id
 */
export interface EmployeesWithoutGoalsRow {
  employee_name: string;
  employee_email: string;
  department_name: string;
  job_title: string;
}

/**
 * Stale Goals Employees row (table)
 * Columns: employee_name, employee_email, department_name, last_updated
 * Filters: cycle_id, department_id
 */
export interface StaleGoalsEmployeesRow {
  employee_name: string;
  employee_email: string;
  department_name: string;
  last_updated: string;
}

// ============================================
// Cycles Widget Response Types
// ============================================

/**
 * Cycles Overview Summary response (multi_stat)
 * Filters: org_id
 */
export interface CyclesOverviewSummaryResponse {
  active_cycles: number;
  completion_average: number;
  at_risk_cycles: number;
  employees_involved: number;
}

/**
 * Trend direction for KPI cards
 */
export interface TrendInfo {
  direction: 'up' | 'down';
  percentage: number;
}

/**
 * Active Cycles count response (kpi_card/gauge)
 * Filters: org_id, cycle_type
 */
export interface CyclesActiveCountResponse {
  value: number;
  trend?: TrendInfo;
}

/**
 * Completion Average response (kpi_card with %)
 * Filters: org_id
 */
export interface CyclesCompletionAverageResponse {
  value: number;
  suffix?: string;
  trend?: TrendInfo;
}

/**
 * At-Risk Cycles count response (kpi_card with alert)
 * Filters: org_id
 */
export interface CyclesAtRiskCountResponse {
  value: number;
  alert?: boolean;
  alert_threshold?: number;
  trend?: TrendInfo;
}

/**
 * Cycles by Type item (donut/pie/bar)
 * Filters: org_id
 */
export interface CyclesByTypeItem {
  cycle_type: string;
  count: number;
  percentage: number;
}

/**
 * Top Cycles by Completion item (horizontal bar)
 * Filters: org_id
 * Note: Returns top 10 by default
 */
export interface CyclesByCompletionItem {
  cycle_name: string;
  completion_percentage: number;
}

/**
 * Cycles List row (table)
 * Columns: cycle_name, cycle_type, participants, completion_percentage, is_at_risk
 * Filters: org_id, cycle_type
 */
export interface CyclesListRow {
  cycle_name: string;
  cycle_type: string;
  participants: number;
  completion_percentage: number;
  is_at_risk: boolean;
}

/**
 * At-Risk Cycles List row (table)
 * Columns: cycle_name, overdue_stage, participants, completion_percentage
 * Filters: org_id
 */
export interface CyclesAtRiskListRow {
  cycle_name: string;
  overdue_stage: string;
  participants: number;
  completion_percentage: number;
}

// ============================================
// Filter Registry Types
// ============================================

/**
 * Filter type from the registry
 */
export type FilterType = 'static' | 'dynamic';

/**
 * Selection type for filters
 */
export type SelectionType = 'single' | 'multiselect';

/**
 * Filter option returned from the registry
 */
export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Filter definition from the registry (list endpoint)
 */
export interface FilterDefinition {
  key: string;
  name: string;
  description: string;
  type: FilterType;
}

/**
 * Filter options response from the registry (options endpoint)
 */
export interface FilterOptionsResponse {
  filter_key: string;
  filter_name: string;
  filter_type: FilterType;
  description: string;
  options: FilterOption[];
}

/**
 * Widget filter configuration in config_schema
 */
export interface WidgetFilterConfig {
  filter_key: string;
  /** Display label for the filter (from widget template) */
  filter_label?: string;
  filter_type: FilterType;
  selection_type: SelectionType;
  required: boolean;
  default_value: string | string[];
}

/**
 * Widget filter value stored in query_config
 */
export interface WidgetFilterValue {
  filter_key: string;
  value: string | string[];
}

// ============================================
// Widget Capabilities Types
// ============================================

/**
 * Chart type options available for widgets
 */
export type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'column' | 'kpi_card' | 'donut' | 'map' | 'table' | 'gauge' | 'multi_stat';

/**
 * Chart configuration option definition
 */
export interface ChartConfigOption {
  type: 'boolean' | 'select' | 'number';
  default: any;
  options?: string[]; // for select type
}

/**
 * Chart-specific configuration
 */
export interface ChartConfig {
  options: Record<string, ChartConfigOption>;
}

/**
 * Layout constraints for widget sizing
 */
export interface WidgetLayoutConstraints {
  min_width: number;
  max_width: number;
  min_height: number;
  max_height: number;
  default_width: number;
  default_height: number;
}

/**
 * Query capabilities for a widget
 */
export interface WidgetQueryCapabilities {
  required_dimensions: string[];
  filters: WidgetFilterConfig[];
}

/**
 * Visualization capabilities for a widget
 */
export interface WidgetVisualizationCapabilities {
  layout: WidgetLayoutConstraints;
  allowed_chart_types: ChartType[];
  chart_configs: Record<string, ChartConfig>;
}

/**
 * Complete widget capabilities
 */
export interface WidgetCapabilities {
  query: WidgetQueryCapabilities;
  visualization: WidgetVisualizationCapabilities;
}

/**
 * Widget visualization config (user's current selection)
 */
export interface WidgetVisualizationConfig {
  chart_type: ChartType;
  [key: string]: any;
}
